import React, { useEffect, useState } from 'react';
import { Drawer, Spin, Tag, Empty, Button, Divider } from 'antd';
import {
  ClockCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import moment from 'moment-jalaali';
import { myCalendarApi } from '../../utils/api';
import { resolveFaIconClasses } from '../../utils/faIconUtils';

const TYPE_FALLBACK = {
  follow_up: 'پیگیری',
  follow_ups: 'پیگیری',
  task: 'وظیفه',
  tasks: 'وظیفه',
  events: 'رویداد',
  event: 'رویداد',
  tickets: 'تیکت',
  ticket: 'تیکت',
  leaves: 'مرخصی',
  leave: 'مرخصی',
};

const formatEventTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
};

const formatJalaliTitle = (jalaliKey) => {
  if (!jalaliKey) return '';
  const m = moment(jalaliKey, 'jYYYY-jMM-jDD');
  if (!m.isValid()) return jalaliKey;
  return m.format('dddd jD jMMMM jYYYY');
};

export default function CalendarDayDrawer({
  open,
  onClose,
  jalaliDate,
  filterType = 'all',
  typesMeta = {},
}) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!open || !jalaliDate) return;

    let active = true;
    setLoading(true);
    myCalendarApi
      .getDay({ date: jalaliDate, type: filterType })
      .then((res) => {
        if (!active) return;
        const payload = res?.data || res || {};
        setItems(Array.isArray(payload.items) ? payload.items : []);
        setSummary(payload.summary || null);
      })
      .catch(() => {
        if (active) {
          setItems([]);
          setSummary(null);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, jalaliDate, filterType]);

  const resolveTypeLabel = (type) =>
    typesMeta[type] || typesMeta[`${type}s`] || TYPE_FALLBACK[type] || type || 'فعالیت';

  return (
    <Drawer
      title={
        <div style={{ fontFamily: 'CustomFont' }}>
          <div className="text-base font-bold">{formatJalaliTitle(jalaliDate)}</div>
          {summary?.total != null ? (
            <div className="text-xs text-gray-500 font-normal mt-0.5">
              {summary.total} فعالیت
            </div>
          ) : null}
        </div>
      }
      placement="left"
      width={420}
      open={open}
      onClose={onClose}
      destroyOnClose
      className="custom-font"
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Spin />
        </div>
      ) : items.length === 0 ? (
        <Empty description="فعالیتی برای این روز ثبت نشده" />
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const details = item.details || {};
            const activity = details.activity || {};
            const bg = item?.extendedProps?.bg_color || activity.color || '#3b82f6';
            const iconInfo = resolveFaIconClasses(item?.extendedProps?.icon || activity.icon);
            const iconClass = iconInfo?.className || 'fa-solid fa-calendar';
            const typeLabel = resolveTypeLabel(item.event_type);

            return (
              <div
                key={`${item.event_type}-${item.id}`}
                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden shadow-sm"
              >
                <div className="h-1" style={{ backgroundColor: bg }} />
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 text-white text-sm"
                        style={{ backgroundColor: bg }}
                      >
                        <i className={iconClass} aria-hidden />
                      </span>
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-800 dark:text-gray-100 truncate">
                          {item.title || 'بدون عنوان'}
                        </div>
                        <Tag className="mt-1" style={{ fontFamily: 'CustomFont' }}>
                          {typeLabel}
                        </Tag>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 shrink-0 flex items-center gap-1">
                      <ClockCircleOutlined />
                      {formatEventTime(item.start)}
                    </div>
                  </div>

                  {details.status_label ? (
                    <Tag
                      color={details.status === 'completed' || details.status === 'done' ? 'green' : 'orange'}
                      className="mb-2"
                    >
                      {details.status_label}
                    </Tag>
                  ) : null}

                  {details.remark ? (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 leading-relaxed">
                      <FileTextOutlined className="ml-1 text-gray-400" />
                      {details.remark}
                    </p>
                  ) : null}

                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                    {details.contact_name ? (
                      <span className="flex items-center gap-1">
                        <UserOutlined />
                        {details.contact_name}
                      </span>
                    ) : null}
                    {details.contact_mobile ? (
                      <span className="flex items-center gap-1 dir-ltr">
                        <PhoneOutlined />
                        {details.contact_mobile}
                      </span>
                    ) : null}
                    {details.assigned_to?.name ? (
                      <span className="flex items-center gap-1">
                        <UserOutlined />
                        واگذار: {details.assigned_to.name}
                      </span>
                    ) : null}
                    {activity.name ? (
                      <span>{activity.name}</span>
                    ) : null}
                    {details.duration ? (
                      <span>{details.duration} دقیقه</span>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Divider />
      <div className="flex justify-end">
        <Button onClick={onClose} style={{ fontFamily: 'CustomFont' }}>
          بستن
        </Button>
      </div>
    </Drawer>
  );
}
